import React, { forwardRef } from 'react';
import CodeInput from './CodeInput';

// 2FA v2 code-entry surface. Renders one of two variants:
//   - 'otp':         "Enter the code from your authenticator app or SMS"
//   - 'email_code':  "Check your email for the 6-digit code"
// Submission shape is identical (PUT /sessions/:id/2fa with method='otp')
// because the backend RPUSHes the same key — only the seller-facing copy
// differs. Step 5 and Step 10 wire the submit handler.
// iter-39: forwardRef so the parent step can imperatively clear the
// underlying CodeInput when LinkedIn rejects a code (otherwise stale
// digits would re-fire onComplete + the seller can't type fresh ones).
const CodeEntryPanel = forwardRef(function CodeEntryPanel({
  variant,
  code,
  onCodeComplete,
  onSubmit,
  onResend,
  loading,
  verifying,
  resending,
  resendsLeft,
  error,
  submitLabel,
  inboxEmail,
  // iter-21: when the parent step renders the inbox-aware copy as the
  // StepLayout title (Step17 email-change-2FA), suppress the inline
  // headline + supporting line here so the seller doesn't see the same
  // sentence twice. Defaults to false to keep the legacy login-2FA
  // caller (Step07) unchanged.
  compactHeader,
}, ref) {
  const isEmail = variant === 'email_code';

  // iter-12: include the specific inbox address when known. The
  // email-change-2FA path knows exactly which inbox LinkedIn sent the
  // code to (current primary), so naming it removes ambiguity for the
  // seller. Falls back to the generic copy when the address isn't
  // wired in (defensive for the login-2FA caller, which currently
  // doesn't pass inboxEmail).
  const headline = isEmail
    ? (inboxEmail
        ? `Check your ${inboxEmail} inbox for the 6-digit code`
        : 'Check your email for the 6-digit code')
    : 'Enter the 6-digit code from your authenticator app or SMS';
  const supporting = isEmail
    ? (inboxEmail
        ? `LinkedIn sent the code to ${inboxEmail}. Paste it below.`
        : 'LinkedIn just sent you a code. Paste it below.')
    : 'LinkedIn sends it via whichever method is enabled on your account.';

  const errorStyle = {
    color: 'var(--danger, #ef4444)',
    fontSize: 13,
    marginTop: 8,
    textAlign: 'center',
  };

  const disabled = loading || verifying || code.length < 6;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {!compactHeader && (
        <>
          <p style={{
            color: 'var(--text)',
            fontSize: 14,
            textAlign: 'center',
            margin: 0,
            fontWeight: 600,
          }}>
            {headline}
          </p>
          <p style={{
            color: 'var(--text2)',
            fontSize: 13,
            textAlign: 'center',
            margin: 0,
            lineHeight: 1.5,
          }}>
            {supporting}
          </p>
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <CodeInput ref={ref} length={6} onComplete={onCodeComplete} />
      </div>

      {error && <p style={errorStyle}>{error}</p>}

      <button
        onClick={onSubmit}
        disabled={disabled}
        style={{
          background: disabled ? 'var(--primary-soft)' : 'var(--primary)',
          color: '#fff',
          padding: 14,
          borderRadius: 8,
          border: 'none',
          fontSize: 16,
          fontWeight: 600,
          width: '100%',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.7 : 1,
        }}
      >
        {verifying
          ? 'Verifying with LinkedIn...'
          : loading
            ? 'Submitting...'
            : (submitLabel || 'Verify Code')}
      </button>

      {onResend && (
        <button
          type="button"
          onClick={onResend}
          disabled={resending || resendsLeft <= 0}
          style={{
            background: 'none',
            border: 'none',
            color: (resending || resendsLeft <= 0) ? 'var(--text2)' : 'var(--primary)',
            fontSize: 14,
            cursor: (resending || resendsLeft <= 0) ? 'not-allowed' : 'pointer',
            padding: '8px 0',
            textAlign: 'center',
            opacity: (resending || resendsLeft <= 0) ? 0.5 : 1,
          }}
        >
          {resending
            ? 'Sending...'
            : resendsLeft > 0
              ? `Resend Code (${resendsLeft} remaining)`
              : 'No resends remaining'}
        </button>
      )}
    </div>
  );
});

export default CodeEntryPanel;
